import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Shield,
  Ship,
  ChevronLeft,
  Camera,
  FileText,
  X,
  MessageSquare,
  Paperclip,
  ImagePlus,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useAuth } from "@/context/AuthContext";
import { useCustomerContext } from "@/context/CustomerContext";
import { useChat } from "@/hooks/useChat";
import { ChatMessage } from "@/types";
import AnayaIcon from "@/assets/anaya.jpg";
import ProtegaLogo from "@/assets/protegalogo.png";
import axiosInstance from "@/service/axios";
import { RootState, AppDispatch } from "@/store/store";
import {
  openConfirmSubmitModal,
  closeConfirmSubmitModal,
  clearCurrentChat,
  clearSubmittedFromChat,
} from "@/store/slices/chatSlice";
import {
  clearOpenClaimByClaimId,
  getPolicyClaimAction,
  rememberOpenClaim,
} from "@/utils/openClaims";
import { Policy, ChatReplyOption } from "@/types";
import ChatReplyActions from "./ChatReplyActions";

const ChatBot: React.FC = () => {
  const navigate = useNavigate();
  const { claimId: routeClaimId } = useParams<{ claimId: string }>();
  const { pathname } = useLocation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const prevChatRef = useRef<ChatMessage[]>([]);

  // Refs for Custom Camera
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { showConfirmSubmitModal } = useSelector(
    (state: RootState) => state.chat,
  );

  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");
  const [pendingFeedback, setPendingFeedback] = useState(false);
  const MAX_FILES = 10;
  const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB

  // NEW: State for Custom Camera & Pending Files
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [chatDragActive, setChatDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const messageTimestamps = useRef<Record<number, string>>({});

  const [triageReplies, setTriageReplies] = useState<Record<string, string>>(
    {},
  );
  const [triageLoading, setTriageLoading] = useState<Record<string, boolean>>(
    {},
  );

  const { user } = useAuth();
  const { claims, refreshClaims } = useCustomerContext();

  const {
    currentChat,
    isLoading,
    isUploading,
    submittedFromChat,
    policies,
    selectedPolicyId,
    claimId,
    message,
    setMessage,
    handleFetchPolicies,
    handleFetchClaims,
    handleOpenClaim,
    handleSendMessage,
    handleUploadEvidence,
    handleSubmitClaim,
    handleSetSelectedPolicy,
    handleSetClaimId,
  } = useChat();

  const sendImageTriage = async (
    imageUrls: string[],
    chat: ChatMessage[],
    claim: string,
    helperText?: string,
  ) => {
    const firstUrl = imageUrls[0] || "";
    if (firstUrl) {
      setTriageLoading((prev) => ({ ...prev, [firstUrl]: true }));
    }
    try {
      const res = await axiosInstance.post("/api/triage/chatImage", {
        image_url: imageUrls,
        claim_id: claim,
        messagesArr: chat,
        helper_text: helperText,
      });
      const triageMsgText = res?.data?.message;
      const success = res?.data?.success;

      if (
        success &&
        typeof triageMsgText === "string" &&
        triageMsgText.trim() &&
        firstUrl
      ) {
        setTriageReplies((prev) => ({ ...prev, [firstUrl]: triageMsgText }));
      }
      return { success, message: triageMsgText ?? "" };
    } catch (error) {
      console.error("Failed to send data to triage API:", error);
      return { success: false, message: "" };
    } finally {
      if (firstUrl) {
        setTriageLoading((prev) => ({ ...prev, [firstUrl]: false }));
      }
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await handleFetchPolicies();
      } catch (err) {
        console.error("Failed to fetch policies:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (routeClaimId) handleSetClaimId(routeClaimId);
      try {
        await handleFetchClaims();
      } catch (err) {
        console.error("Failed to fetch claims:", err);
      }
    })();
  }, [routeClaimId]);

  useEffect(() => {
    if (pathname === "/customer/chat" && !routeClaimId) {
      setTimeout(() => setShowPolicyModal(true), 1500);
    }
  }, [pathname, routeClaimId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentChat, triageReplies, triageLoading]);

  useEffect(() => {
    if (rating !== null && rating >= 8) setFeedback("");
  }, [rating]);

  useEffect(() => {
    prevChatRef.current = currentChat;
  }, []);

  // The customer submitted from the chat ("Submit claim" typed or tapped): same follow-up as the Submit button.
  useEffect(() => {
    if (!submittedFromChat) return;
    dispatch(clearSubmittedFromChat());
    if (claimId) clearOpenClaimByClaimId(claimId);
    void refreshClaims();
    setPendingFeedback(true);
  }, [submittedFromChat]);

  // Watch for when AI finishes responding after claim submission
  useEffect(() => {
    if (pendingFeedback && !isLoading) {
      const timer = setTimeout(() => {
        setIsFeedbackModalOpen(true);
        setPendingFeedback(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [pendingFeedback, isLoading]);

  useEffect(() => {
    const newMessages = currentChat.filter(
      (msg) => !prevChatRef.current.includes(msg),
    );

    if (newMessages.length > 0 && claimId) {
      const newUserMediaMsg = newMessages.find(
        (msg) =>
          msg.role === "user" &&
          msg.content.some(
            (c) => c.type === "image_url" || c.type === "video_url",
          ),
      );

      if (newUserMediaMsg) {
        const imageUrls = newUserMediaMsg.content
          .filter((c) => c.type === "image_url" && c.image_url?.url)
          .map((c) => c.image_url!.url);

        const textContent = newUserMediaMsg.content.find((c) => c.type === "text") as { type: "text"; text: string } | undefined;
        const helperText = textContent?.text || "";

        if (imageUrls.length > 0) {
          void sendImageTriage(imageUrls, currentChat, claimId, helperText);
        }
      }
    }

    prevChatRef.current = currentChat;
  }, [currentChat, claimId]);
  // NEW: Effect to attach stream to video element when camera opens
  useEffect(() => {
    if (isCameraOpen && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
      videoRef.current
        .play()
        .catch((e) => console.error("Error playing video stream:", e));
    }
  }, [isCameraOpen, cameraStream]);

  // Capture timestamps for new messages
  useEffect(() => {
    const ts = new Date().toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    currentChat.forEach((_msg, idx) => {
      if (!(idx in messageTimestamps.current)) {
        messageTimestamps.current[idx] = ts;
      }
    });
  }, [currentChat]);

  // Close attach menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        attachMenuRef.current &&
        !attachMenuRef.current.contains(event.target as Node)
      ) {
        setShowAttachMenu(false);
      }
    };
    if (showAttachMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAttachMenu]);

  /** Centralized helper to add files with validation */
  const addFiles = (newFiles: File[]) => {
    setPendingFiles((prev) => {
      const combined = [...prev, ...newFiles];
      if (combined.length > MAX_FILES) {
        toast.error(`You can upload a maximum of ${MAX_FILES} files.`);
        return prev;
      }
      const totalSize = combined.reduce((sum, f) => sum + f.size, 0);
      if (totalSize > MAX_TOTAL_SIZE) {
        toast.error(`Total file size cannot exceed 20MB.`);
        return prev;
      }
      return combined;
    });
  };

  // --- CUSTOM CAMERA LOGIC ---

  const startCamera = async () => {
    try {
      // Constraints to prefer back camera on mobile, or default on desktop
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });
      setCameraStream(stream);
      setIsCameraOpen(true);
    } catch (err) {
      console.error("Failed to access camera:", err);
      toast.error("Could not access camera. Please allow camera permissions.");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const context = canvas.getContext("2d");
      if (context) {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to Blob/File
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const fileName = `claim_capture_${Date.now()}.jpg`;
              const file = new File([blob], fileName, { type: "image/jpeg" });

              addFiles([file]);
              stopCamera();
            }
          },
          "image/jpeg",
          0.9,
        );
      }
    }
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // --- GALLERY / FILE SELECT ---
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const validFiles = Array.from(files).filter((f) =>
        f.type.startsWith("image/") || f.type.startsWith("video/")
      );
      addFiles(validFiles);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- DRAG & DROP ON CHAT ---
  const handleChatDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setChatDragActive(true);
    } else if (e.type === "dragleave") {
      setChatDragActive(false);
    }
  };

  const handleChatDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setChatDragActive(false);
    const files = e.dataTransfer.files;
    if (files) {
      const validFiles = Array.from(files).filter((f) =>
        f.type.startsWith("image/") || f.type.startsWith("video/")
      );
      addFiles(validFiles);
    }
  };

  const handleMessageSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!message.trim() && pendingFiles.length === 0) || !claimId) return;

    try {
      let uploadedUrls: string[] = [];
      const uploadedMimes: Record<string, string> = {};

      // 1. Upload Pending Files FIRST
      if (pendingFiles.length > 0) {
        const formData = new FormData();
        pendingFiles.forEach((file) => {
          formData.append("files", file);
        });
        formData.append("claimId", claimId);
        formData.append("claim_id", claimId);

        const uploadRes = await handleUploadEvidence(formData);
        if (uploadRes && Array.isArray(uploadRes.uploadedMetadata)) {
          uploadedUrls = uploadRes.uploadedMetadata.map((f: any) => f.url);
          uploadRes.uploadedMetadata.forEach((f: any) => {
            if (typeof f?.mime === "string") uploadedMimes[f.url] = f.mime;
          });
        }
        // Some files were saved and some weren't (e.g. a video over 60 seconds).
        if (typeof uploadRes?.warning === "string") toast.error(uploadRes.warning);

        // Only clear files if upload succeeded
        setPendingFiles([]);
      }

      // 2. Send Combined Message
      const contentBlocks: any[] = [];
      
      // If we have text message, add it as first block
      if (message.trim()) {
        contentBlocks.push({ type: "text", text: message });
      }

      // Add all uploaded images/videos as blocks
      uploadedUrls.forEach((url) => {
        // Private uploads come back as signed links (?token=…), so prefer the type the server reports.
        const path = url.toLowerCase().split("?")[0];
        const isVideoFile = uploadedMimes[url]
          ? uploadedMimes[url].startsWith("video/")
          : path.endsWith(".mp4") || path.endsWith(".webm") || path.endsWith(".mov");
        if (isVideoFile) {
          contentBlocks.push({ type: "video_url", video_url: { url } });
        } else {
          contentBlocks.push({ type: "image_url", image_url: { url } });
        }
      });

      if (contentBlocks.length > 0) {

        const newMessage: ChatMessage = {
          role: "user",
          content: contentBlocks,
          timestamp: new Date().toISOString(),
        };
        await handleSendMessage(newMessage);
        setMessage("");
      }
    } catch (err: any) {
      console.error("Failed to send message/upload", err);
      toast.error(
        typeof err?.message === "string" && err?.success === false
          ? err.message
          : "Failed to process request. Please try again.",
      );
    }
  };

  /** A reply button or list row: Submit goes through the confirm modal; the rest are sent as the customer's reply. */
  const handleOptionClick = async (option: ChatReplyOption) => {
    if (!claimId || isLoading || isUploading) return;
    if (option.id === "SUBMIT") {
      dispatch(openConfirmSubmitModal());
      return;
    }
    await handleSendMessage({
      role: "user",
      content: [{ type: "text", text: option.label }],
      timestamp: new Date().toISOString(),
    });
  };

  const handleConfirmedSubmit = async () => {
    dispatch(closeConfirmSubmitModal());
    try {
      await handleSubmitClaim();
      if (claimId) clearOpenClaimByClaimId(claimId);
      await refreshClaims();
      setPendingFeedback(true);
    } catch (err) {
      console.error(err);
      setPendingFeedback(false);
    }
  };

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalOpen(false);
    setRating(null);
    setFeedback("");
    setTriageReplies({});
    setTriageLoading({});
    dispatch(clearCurrentChat());
    navigate("/customer/claims");
  };

  const handleFeedbackSubmit = async () => {
    if (!rating) {
      toast.error("Please select a rating before submitting.");
      return;
    }
    try {
      const feedbackString =
        rating < 8 && feedback.trim()
          ? `${rating}/10 - ${feedback.trim()}`
          : `${rating}/10`;

      await axiosInstance.post("api/claim/createCustomerFeedback", {
        claim_id: claimId,
        customer_feedback: feedbackString,
      });
      setRating(null);
      setFeedback("");
      setIsFeedbackModalOpen(false);
      setTriageReplies({});
      setTriageLoading({});
      dispatch(clearCurrentChat());
      navigate("/customer/claims");
    } catch (err) {
      console.error("Failed to submit feedback:", err);
    }
  };

  const TypingIndicator = () => (
    <div className="tw-flex tw-items-center tw-gap-1.5 tw-px-1">
      <span
        className="tw-w-2 tw-h-2 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
        style={{ animationDelay: "0ms" }}
      />
      <span
        className="tw-w-2 tw-h-2 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
        style={{ animationDelay: "150ms" }}
      />
      <span
        className="tw-w-2 tw-h-2 tw-bg-gray-500 tw-rounded-full tw-animate-bounce"
        style={{ animationDelay: "300ms" }}
      />
    </div>
  );

  const formatTime = (msg?: ChatMessage, index?: number) => {
    if (msg?.timestamp) {
      return new Date(msg.timestamp).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }
    if (index !== undefined && index in messageTimestamps.current) {
      return messageTimestamps.current[index];
    }
    return new Date().toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="tw-flex tw-flex-col tw-h-[100dvh] tw-bg-[#F2F2F2]">
      {/* ─── Custom Camera Modal Overlay ─────────────────────────────────────────── */}
      <AnimatePresence>
        {isCameraOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tw-fixed tw-inset-0 tw-z-[10000] tw-bg-black tw-flex tw-flex-col"
          >
            {/* Camera Header */}
            <div className="tw-flex tw-justify-between tw-items-center tw-p-4 tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-z-10">
              <span className="tw-text-white tw-font-medium">Take Photo</span>
              <button
                onClick={stopCamera}
                className="tw-p-2 tw-bg-black/50 tw-rounded-full tw-text-white"
              >
                <X size={24} />
              </button>
            </div>

            {/* Video Preview */}
            <div className="tw-flex-1 tw-relative tw-flex tw-items-center tw-justify-center tw-bg-black tw-overflow-hidden">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="tw-w-full tw-h-full tw-object-cover"
              />
              <canvas ref={canvasRef} className="tw-hidden" />
            </div>

            {/* Camera Controls */}
            <div className="tw-bg-black tw-pb-10 tw-pt-6 tw-flex tw-items-center tw-justify-center tw-gap-8">
              <button
                onClick={capturePhoto}
                className="tw-w-20 tw-h-20 tw-rounded-full tw-border-4 tw-border-white tw-flex tw-items-center tw-justify-center tw-bg-transparent hover:tw-bg-white/20 tw-transition-colors"
              >
                <div className="tw-w-16 tw-h-16 tw-bg-white tw-rounded-full" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Header ─────────────────────────────────────────── */}
      <header
        className="tw-bg-white tw-sticky tw-top-0 tw-z-50"
        style={{ boxShadow: "0 4px 6px -1px #353535" }}
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-3 tw-max-w-4xl tw-mx-auto tw-relative">
          <button
            onClick={() => {
              if (window.history.length > 2) navigate(-1);
              else navigate("/customer");
            }}
            className="tw-absolute tw-left-4 tw-p-2 tw-rounded-full hover:tw-bg-[#F2F2F2] tw-transition-colors"
          >
            <ChevronLeft size={24} className="tw-text-[#2a2a2a]" />
          </button>

          <img
            src={ProtegaLogo}
            alt="Protega"
            className="tw-h-10 tw-object-contain"
          />
        </div>
      </header>

      {/* ─── Chat Area ─────────────────────────────────────────── */}
      <main
        ref={chatContainerRef}
        className={`tw-flex-1 tw-overflow-y-auto tw-px-4 tw-py-6 tw-relative ${chatDragActive ? "tw-ring-2 tw-ring-inset tw-ring-[#00c5fb]/40 tw-bg-[#00c5fb]/5" : ""
          }`}
        onDragEnter={handleChatDrag}
        onDragLeave={handleChatDrag}
        onDragOver={handleChatDrag}
        onDrop={handleChatDrop}
      >
        {/* Drag overlay hint */}
        <AnimatePresence>
          {chatDragActive && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-bg-white/90 tw-backdrop-blur-md tw-p-4"
            >
              <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-w-full tw-h-full tw-border-2 tw-border-dashed tw-border-[#00C5FB] tw-rounded-2xl">
                <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center">
                  <ImagePlus size={32} className="tw-text-[#00C5FB]" />
                </div>
                <p className="tw-text-[#2a2a2a] tw-font-semibold tw-text-base">Drop your images here</p>
                <p className="tw-text-gray-400 tw-text-xs">Supports JPG, PNG, HEIC</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className="tw-max-w-4xl tw-mx-auto">
          {!claimId ? (
            /* ─── Empty State ─────────────────────────────────────────── */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-12"
            >
              <div className="tw-w-20 tw-h-20 tw-bg-[#2a2a2a] tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mb-6 tw-shadow-lg">
                <MessageSquare size={40} className="tw-text-[#00c5fb]" />
              </div>
              <h2 className="tw-text-2xl tw-font-bold tw-text-[#2a2a2a] tw-mb-2">
                Welcome to Protega
              </h2>
              <p className="tw-text-gray-500 tw-mb-10 tw-text-center tw-max-w-sm">
                File a claim for your protected products quickly and easily
              </p>

              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowPolicyModal(true)}
                className="tw-px-10 tw-py-3.5 tw-bg-[#00C5FB] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-full tw-shadow-lg hover:tw-bg-[#34d3ff] tw-transition-colors"
              >
                Start New Claim
              </motion.button>
            </motion.div>
          ) : (
            /* ─── Messages ─────────────────────────────────────────── */
            <div className="tw-space-y-5">
              {currentChat.filter((msg) => msg.role !== "system").map((msg, index, arr) => {
                const prev = arr[index - 1];
                const assistantText =
                  msg.content?.[0]?.type === "text" ? msg.content[0].text : "";
                const assistantIsEvidenceReply =
                  typeof assistantText === "string" &&
                  (assistantText.startsWith("Thank you for uploading") ||
                    assistantText.startsWith("Thanks for sharing the image"));
                const prevIsUserImage =
                  prev?.role === "user" &&
                  prev.content.some((c) => c.type === "image_url");

                if (
                  msg.role === "assistant" &&
                  assistantIsEvidenceReply &&
                  prevIsUserImage
                ) {
                  const prevImageContent = prev.content.find(
                    (c) => c.type === "image_url" && c.image_url?.url,
                  ) as
                    | { type: "image_url"; image_url: { url: string } }
                    | undefined;
                  const imageUrl = prevImageContent?.image_url?.url;
                  const triage = imageUrl ? triageReplies[imageUrl] : undefined;

                  if (triage) {
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="tw-flex tw-items-end tw-gap-3"
                      >
                        <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md">
                          <img
                            src={AnayaIcon}
                            alt="Anaya"
                            className="tw-w-full tw-h-full tw-object-cover"
                          />
                        </div>
                        <div className="tw-max-w-[75%]">
                          <div className="tw-bg-[#E8E8E8] tw-text-[#2a2a2a] tw-rounded-2xl tw-rounded-bl-sm tw-px-4 tw-py-3 tw-shadow-sm">
                            <p className="tw-text-sm tw-leading-relaxed">
                              {triage}
                            </p>
                          </div>
                          <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-ml-1">
                            {formatTime(msg, index)}
                          </p>
                        </div>
                      </motion.div>
                    );
                  }

                  if (imageUrl && triageLoading[imageUrl]) {
                    return (
                      <motion.div
                        key={index + "-triage-loader"}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="tw-flex tw-items-end tw-gap-3"
                      >
                        <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md">
                          <img
                            src={AnayaIcon}
                            alt="Anaya"
                            className="tw-w-full tw-h-full tw-object-cover"
                          />
                        </div>
                        <div className="tw-bg-[#E8E8E8] tw-rounded-2xl tw-rounded-bl-sm tw-px-4 tw-py-4 tw-shadow-sm">
                          <TypingIndicator />
                        </div>
                      </motion.div>
                    );
                  }
                }

                const isUser = msg.role === "user";
                const isError = !isUser && msg.messageType === "error";
                // Like WhatsApp, only the latest reply's buttons or list can be used.
                const showActions = !isUser && Boolean(msg.ui) && index === arr.length - 1;

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: isUser ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`tw-flex tw-items-end tw-gap-3 ${isUser ? "tw-flex-row-reverse" : ""}`}
                  >
                    {!isUser && (
                      <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md">
                        <img
                          src={AnayaIcon}
                          alt="Anaya"
                          className="tw-w-full tw-h-full tw-object-cover"
                        />
                      </div>
                    )}

                    {isUser && (
                      <div className="tw-w-9 tw-h-9 tw-shrink-0" />
                    )}

                    <div className="tw-max-w-[75%]">
                      <div
                        className={`tw-px-4 tw-py-3 tw-text-sm tw-leading-relaxed tw-shadow-sm ${isUser
                            ? "tw-bg-[#D0D0D0] tw-text-[#2a2a2a] tw-rounded-2xl tw-rounded-br-sm"
                            : isError
                              ? "tw-bg-red-50 tw-text-red-800 tw-border tw-border-red-200 tw-rounded-2xl tw-rounded-bl-sm"
                              : "tw-bg-[#E8E8E8] tw-text-[#2a2a2a] tw-rounded-2xl tw-rounded-bl-sm"
                          }`}
                      >
                        {msg.content.map((content, i) => (
                          <div key={i}>
                            {content.type === "text" && <p className="tw-whitespace-pre-line">{content.text}</p>}

                            {content.type === "image_url" &&
                              content.image_url?.url && (
                                <img
                                  src={content.image_url.url}
                                  className="tw-rounded-xl tw-mt-2 tw-max-w-[220px] tw-h-auto tw-object-cover"
                                  alt="evidence"
                                />
                              )}

                            {content.type === "video_url" &&
                              content.video_url?.url && (
                                <video
                                  controls
                                  playsInline
                                  preload="metadata"
                                  controlsList="nodownload noremoteplayback noplaybackrate nofullscreen"
                                  src={content.video_url.url}
                                  className="tw-rounded-xl tw-mt-2 tw-max-w-[280px]"
                                />
                              )}

                            {(content as any).type === "pdf_url" &&
                              (content as any).pdf_url?.url && (
                                <a
                                  href={(content as any).pdf_url.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="tw-flex tw-items-center tw-gap-2 tw-bg-white/50 tw-p-3 tw-rounded-xl hover:tw-bg-white/80 tw-transition-colors tw-mt-2"
                                >
                                  <FileText
                                    size={18}
                                    className="tw-text-red-500 tw-shrink-0"
                                  />
                                  <span className="tw-truncate tw-text-sm tw-font-medium tw-text-[#2a2a2a]">
                                    {(content as any).pdf_url.file_name ||
                                      "View PDF"}
                                  </span>
                                </a>
                              )}
                          </div>
                        ))}
                      </div>
                      {showActions && msg.ui && (
                        <ChatReplyActions
                          ui={msg.ui}
                          disabled={isLoading || isUploading}
                          onSelect={handleOptionClick}
                        />
                      )}
                      <p
                        className={`tw-text-[10px] tw-text-gray-400 tw-mt-1 ${isUser ? "tw-text-right tw-mr-1" : "tw-ml-1"}`}
                      >
                        {formatTime(msg, index)}
                      </p>
                    </div>
                  </motion.div>
                );
              })}

              {/* Uploading Indicator */}
              {isUploading && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="tw-flex tw-items-end tw-gap-3 tw-flex-row-reverse"
                >
                  <div className="tw-w-9 tw-h-9 tw-shrink-0" />
                  <div className="tw-bg-[#D0D0D0] tw-rounded-2xl tw-rounded-br-sm tw-px-4 tw-py-4 tw-shadow-sm">
                    <TypingIndicator />
                  </div>
                </motion.div>
              )}

              {/* AI Loading Indicator */}
              {isLoading && (currentChat.length === 0 || currentChat[currentChat.length - 1]?.role !== "assistant") && (
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="tw-flex tw-items-end tw-gap-3"
                >
                  <div className="tw-w-9 tw-h-9 tw-rounded-full tw-overflow-hidden tw-shrink-0 tw-shadow-md">
                    <img
                      src={AnayaIcon}
                      alt="Anaya"
                      className="tw-w-full tw-h-full tw-object-cover"
                    />
                  </div>
                  <div className="tw-bg-[#E8E8E8] tw-rounded-2xl tw-rounded-bl-sm tw-px-4 tw-py-4 tw-shadow-sm">
                    <TypingIndicator />
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </main>

      {/* ─── Input Area ─────────────────────────────────────────── */}
      <footer className="tw-bg-white tw-border-t tw-border-gray-200 tw-p-4 tw-sticky tw-bottom-0">
        <form
          onSubmit={handleMessageSubmit}
          className="tw-max-w-4xl tw-mx-auto"
        >
          {/* Hidden file input for gallery */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={handleFileSelect}
            className="tw-hidden"
          />

          <div
            className={`tw-bg-[#F2F2F2] tw-border tw-border-gray-200 focus-within:tw-border-[#00c5fb] focus-within:tw-ring-2 focus-within:tw-ring-[#00c5fb]/20 tw-transition-all ${
              pendingFiles.length > 0
                ? "tw-rounded-2xl tw-p-3"
                : "tw-rounded-full tw-p-1.5 tw-pl-2"
            }`}
          >
            {/* Pending Files Preview */}
            {pendingFiles.length > 0 && (
              <div className="tw-flex tw-gap-3 tw-overflow-x-auto tw-pb-3 tw-mb-2">
                {pendingFiles.map((file, idx) => (
                  <div key={idx} className="tw-relative tw-shrink-0 tw-group">
                    <div className="tw-w-[100px] tw-h-[100px] tw-rounded-xl tw-overflow-hidden tw-ring-1 tw-ring-black/10">
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="tw-w-full tw-h-full tw-object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemovePendingFile(idx)}
                      className="tw-absolute tw-top-1 tw-right-1 tw-w-6 tw-h-6 tw-bg-black/60 tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-none tw-cursor-pointer hover:tw-bg-black/80 tw-transition-colors tw-backdrop-blur-sm"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Input Row */}
            <div className="tw-flex tw-items-center tw-gap-2">
              <div className="tw-relative" ref={attachMenuRef}>
                <button
                  type="button"
                  onClick={() => setShowAttachMenu((v) => !v)}
                  disabled={!claimId || isLoading || isUploading}
                  className="tw-p-2.5 tw-rounded-full tw-cursor-pointer hover:tw-bg-[#D3D3D3] tw-transition-colors disabled:tw-opacity-40 disabled:tw-cursor-not-allowed"
                >
                  <Paperclip size={20} className="tw-text-[#2a2a2a]" />
                </button>

                {/* Attach popup menu */}
                <AnimatePresence>
                  {showAttachMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                      className="tw-absolute tw-bottom-full tw-left-0 tw-mb-3 tw-bg-white tw-rounded-2xl tw-overflow-hidden tw-w-[220px] tw-z-50"
                      style={{
                        boxShadow:
                          "0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setShowAttachMenu(false);
                          startCamera();
                        }}
                        className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-[#2a2a2a] tw-bg-white hover:tw-bg-[#F2F2F2] tw-transition-colors tw-border-none tw-cursor-pointer tw-outline-none"
                      >
                        <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                          <Camera size={18} className="tw-text-[#00C5FB]" />
                        </div>
                        <span className="tw-text-sm tw-font-medium tw-whitespace-nowrap">
                          Take Photo
                        </span>
                      </button>
                      <div className="tw-mx-4 tw-border-t tw-border-gray-100" />
                      <button
                        type="button"
                        onClick={() => {
                          setShowAttachMenu(false);
                          fileInputRef.current?.click();
                        }}
                        className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3.5 tw-text-[#2a2a2a] tw-bg-white hover:tw-bg-[#F2F2F2] tw-transition-colors tw-border-none tw-cursor-pointer tw-outline-none"
                      >
                        <div className="tw-w-9 tw-h-9 tw-rounded-full tw-bg-[#00C5FB]/10 tw-flex tw-items-center tw-justify-center tw-shrink-0">
                          <ImagePlus size={18} className="tw-text-[#00C5FB]" />
                        </div>
                        <span className="tw-text-sm tw-font-medium tw-whitespace-nowrap">
                          Choose from Gallery
                        </span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <input
                type="text"
                value={message}
                disabled={!claimId || isLoading || isUploading}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  claimId ? "Type your message..." : "Select a policy to start"
                }
                className="tw-flex-1 tw-bg-transparent tw-text-[#2a2a2a] tw-text-sm tw-outline-none tw-py-2 tw-placeholder-gray-400 disabled:tw-cursor-not-allowed"
              />

              <button
                type="submit"
                disabled={
                  (!message.trim() && pendingFiles.length === 0) ||
                  !claimId ||
                  isLoading ||
                  isUploading
                }
                className="tw-p-2.5 tw-bg-[#00C5FB] tw-rounded-full tw-text-[#2a2a2a] tw-shadow-md hover:tw-bg-[#34d3ff] tw-transition-all disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:tw-shadow-none"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </form>
      </footer>

      {/* ─── Confirm Submit Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showConfirmSubmitModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-backdrop-blur-sm tw-p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="tw-bg-white tw-rounded-2xl tw-w-full tw-max-w-md tw-p-6 tw-shadow-2xl"
            >
              <div className="tw-w-16 tw-h-16 tw-bg-[#00c5fb]/20 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                <Shield size={32} className="tw-text-[#00C5FB]" />
              </div>

              <h2 className="tw-text-xl tw-font-bold tw-text-[#2a2a2a] tw-text-center tw-mb-2">
                Confirm claim details
              </h2>

              <p className="tw-text-sm tw-text-gray-600 tw-text-center tw-mb-6 tw-leading-relaxed">
                Before submitting your claim, please check that you've described
                the problem or uploaded product details (such as photos or
                videos).
                <br />
                <br />
                <span className="tw-text-amber-600 tw-font-medium">
                  Claims submitted without sufficient information may be
                  delayed.
                </span>
              </p>

              <button
                onClick={handleConfirmedSubmit}
                className="tw-w-full tw-py-3.5 tw-bg-[#00C5FB] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-full tw-shadow-lg hover:tw-bg-[#34d3ff] tw-transition-all tw-mb-3"
              >
                Yes, submit my claim
              </button>

              <button
                onClick={() => dispatch(closeConfirmSubmitModal())}
                className="tw-w-full tw-py-3.5 tw-bg-[#F2F2F2] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-full hover:tw-bg-[#D3D3D3] tw-transition-colors"
              >
                Review & add details
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Policy Selection Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {showPolicyModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-end sm:tw-items-center tw-justify-center tw-bg-black/50 tw-backdrop-blur-sm"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="tw-bg-[#1c1c1c] tw-rounded-t-3xl sm:tw-rounded-2xl tw-w-full sm:tw-max-w-md tw-max-h-[80vh] tw-overflow-hidden tw-shadow-2xl"
            >
              <div className="tw-flex tw-items-center tw-justify-between tw-p-5 tw-border-b tw-border-gray-700">
                <h3 className="tw-text-lg tw-font-bold tw-text-white">
                  Select a Policy
                </h3>
                <button
                  onClick={() => setShowPolicyModal(false)}
                  className="tw-p-2 tw-rounded-full hover:tw-bg-[#2a2a2a] tw-transition-colors"
                >
                  <X size={20} className="tw-text-gray-400" />
                </button>
              </div>

              <div className="tw-p-4 tw-space-y-3 tw-overflow-y-auto tw-max-h-[60vh]">
                {policies.length === 0 ? (
                  <div className="tw-text-center tw-py-8 tw-text-gray-400">
                    <Shield
                      size={48}
                      className="tw-mx-auto tw-mb-3 tw-text-gray-600"
                    />
                    <p>No policies found</p>
                  </div>
                ) : (
                  policies.map((item) => {
                    const policyLike = {
                      id: item.id,
                      name: (item as any).policy_number || item.id,
                      type: item.policy_type,
                      premium: 0,
                      expiryDate: (item as any).end_date || "",
                      status: ((item as any).status || "active") as Policy["status"],
                      timeline: [],
                      mode: (item as any).mode,
                      claimId: (item as any).claim_id || (item as any).claimId,
                    } as Policy;
                    const decision = getPolicyClaimAction(policyLike, claims);
                    const actionLabel =
                      decision.action === "resume"
                        ? "Resume"
                        : decision.action === "view"
                          ? "View"
                          : "File";

                    return (
                    <motion.div
                      key={item.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={async () => {
                        try {
                          handleSetSelectedPolicy(item.id);

                          if (decision.action === "view" && decision.claimId) {
                            navigate(`/customer/claims/${decision.claimId}`);
                            setShowPolicyModal(false);
                            return;
                          }

                          const opened = await handleOpenClaim({
                            policyId: item.id,
                            claimId: decision.claimId || "",
                          });
                          const openedId = opened?.id || opened?.claim_id;
                          if (openedId) {
                            rememberOpenClaim(item.id, openedId);
                            navigate(`/customer/chat/${openedId}`);
                          }
                          setShowPolicyModal(false);
                        } catch (err: any) {
                          const body = err?.response?.data ?? err ?? {};
                          const record =
                            body?.data && typeof body.data === "object"
                              ? body.data
                              : body;
                          const existingId =
                            record?.id || record?.claim_id || record?.claimId;
                          const failedMode = String(
                            record?.mode || body?.mode || "",
                          ).toLowerCase();

                          if (failedMode === "submitted" && existingId) {
                            navigate(`/customer/claims/${existingId}`);
                            setShowPolicyModal(false);
                            return;
                          }

                          console.error("Failed to open claim:", err);
                          toast.error(
                            (typeof body?.message === "string" &&
                              body.message) ||
                              "Could not open claim. Try again.",
                          );
                        }
                      }}
                      className={`tw-p-4 tw-rounded-xl tw-border tw-cursor-pointer tw-transition-all ${selectedPolicyId === item.id
                          ? "tw-border-[#00c5fb] tw-bg-[#00c5fb]/10"
                          : "tw-border-gray-700 tw-bg-[#2a2a2a] hover:tw-border-gray-600"
                        }`}
                    >
                      <div className="tw-flex tw-items-center tw-gap-4">
                        <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-bg-[#3a3a3a]">
                          {item.policy_type
                            ?.toLowerCase()
                            .includes("shipping") ? (
                            <Ship size={24} className="tw-text-[#00c5fb]" />
                          ) : (
                            <Shield size={24} className="tw-text-[#00c5fb]" />
                          )}
                        </div>
                        <div className="tw-flex-1">
                          <h4 className="tw-font-semibold tw-text-white">
                            {item.product_title}
                          </h4>
                          <p className="tw-text-sm tw-text-gray-400">
                            {item.policy_type}
                          </p>
                        </div>
                        <span className="tw-text-xs tw-font-medium tw-text-[#00c5fb]">
                          {actionLabel}
                        </span>
                      </div>
                    </motion.div>
                    );
                  })
                )}
              </div>

              <div className="tw-p-4 tw-border-t tw-border-gray-700">
                <button
                  onClick={() => setShowPolicyModal(false)}
                  className="tw-w-full tw-py-3 tw-bg-[#2a2a2a] tw-text-white tw-font-medium tw-rounded-xl hover:tw-bg-[#3a3a3a] tw-transition-colors"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Feedback Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {isFeedbackModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/50 tw-backdrop-blur-sm tw-p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="tw-bg-white tw-rounded-2xl tw-w-full tw-max-w-md tw-p-6 tw-shadow-2xl tw-relative"
            >
              <button
                onClick={handleCloseFeedbackModal}
                className="tw-absolute tw-top-4 tw-right-4 tw-p-2 tw-rounded-full hover:tw-bg-[#F2F2F2] tw-transition-colors"
              >
                <X size={20} className="tw-text-gray-500" />
              </button>

              <div className="tw-text-center tw-mb-6">
                <div className="tw-w-16 tw-h-16 tw-bg-[#00c5fb]/20 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                  <Shield size={32} className="tw-text-[#00c5fb]" />
                </div>
                <h3 className="tw-text-xl tw-font-bold tw-text-[#2a2a2a] tw-mb-1">
                  Claim Submitted!
                </h3>
                <p className="tw-text-sm tw-text-gray-500">
                  How was your experience?
                </p>
              </div>

              <div className="tw-mb-5">
                <div className="tw-flex tw-justify-between tw-gap-1.5">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => (
                    <button
                      key={num}
                      onClick={() => setRating(num)}
                      className={`tw-flex-1 tw-py-2.5 tw-text-sm tw-font-semibold tw-rounded-lg tw-transition-all ${rating === num
                          ? "tw-bg-[#00c5fb] tw-text-[#2a2a2a] tw-shadow-md"
                          : "tw-bg-[#F2F2F2] tw-text-[#2a2a2a] hover:tw-bg-[#E8E8E8]"
                        }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className="tw-flex tw-justify-between tw-text-xs tw-text-gray-500 tw-mt-2 tw-px-1">
                  <span>Not satisfied</span>
                  <span>Very satisfied</span>
                </div>
              </div>

              {rating !== null && rating < 8 && (
                <motion.textarea
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Tell us how we can improve..."
                  className="tw-w-full tw-h-24 tw-bg-[#F2F2F2] tw-border tw-border-gray-300 tw-text-sm tw-p-3 tw-rounded-xl tw-mb-4 tw-text-[#2a2a2a] tw-resize-none focus:tw-ring-2 focus:tw-ring-[#00c5fb]/50 focus:tw-border-[#00c5fb] tw-outline-none tw-transition-all tw-placeholder-gray-400"
                />
              )}

              <button
                onClick={handleFeedbackSubmit}
                disabled={!rating}
                className="tw-w-full tw-py-3.5 tw-bg-[#00C5FB] tw-text-[#2a2a2a] tw-font-semibold tw-rounded-full tw-shadow-lg hover:tw-bg-[#34d3ff] tw-transition-all disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                Send Feedback
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChatBot;
